<template>
  <component
    :is="as"
    @mouseenter="handleHover"
    @mouseleave="handleHoverEnd"
    @click="handleRipple"
    class="flex disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer relative items-center justify-center gap-2 transition-all duration-200"
    :class="[
      buttonClasses,
      { 'opacity-50 cursor-not-allowed': disabled },
      { 
        'animate-svg-path': props.svgAnimation === 'path',
        'animate-svg-rotate': props.svgAnimation === 'rotate' 
      },
      {
        'hover:scale-105': hoverEffect === 'scale',
        'hover:translate-x-2': hoverEffect === 'slide',
        'hover:shadow-glow': hoverEffect === 'glow',
        'hover:mystic-aura': hoverEffect === 'mysticAura',
        'hover:after:scale-x-100': hoverEffect === 'underline',
        'hover:bg-halo': hoverEffect === 'background',
        'overflow-hidden': rippleEffect
      }
    ]"
    :disabled="disabled"
    :aria-busy="loading"
    :aria-label="ariaLabel || text"
    data-hover-animation="border"
  >
    <!-- Loading state -->
    <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-inherit rounded-[30px]">
      <span class="loader"></span>
    </div>

    <!-- Icon slot and prop -->
    <slot name="icon">
      <img
        v-if="icon && iconType !== 'svg'"
        :src="require(`@/assets/icons/svg/${icon}`)"
        class="shrink-0 object-contain"
        :class="[iconClass, { 'opacity-0': loading }]"
        :alt="text"
      />
      <InlineSvg
        v-if="icon && iconType == 'svg'"
        :src="require(`@/assets/icons/svg/${icon}`)"
        class="shrink-0 object-contain"
        :class="[iconClass, { 'opacity-0': loading }]"
      />
    </slot>

    <!-- Text content -->
    <p
      v-show="showText && !loading"
      class="leading-5 transition-colors"
      :class="textClasses"
    >
      {{ text }}
    </p>

    <!-- Counter badge -->
    <div 
      v-if="hasCounter && counterNumber"
      class="counter-badge"
      :class="counterClasses"
    >
      {{ counterNumber }}
    </div>

    <!-- Right icon slot -->
    <slot name="end-icon"></slot>

    <!-- Ripple effect -->
    <div 
      v-if="rippleEffect"
      class="absolute inset-0 pointer-events-none overflow-hidden"
    >
      <span
        v-for="(ripple, index) in ripples"
        :key="index"
        class="absolute bg-white/30 rounded-full animate-ripple origin-center"
        :style="{
          left: ripple.x + 'px',
          top: ripple.y + 'px',
          width: ripple.size + 'px',
          height: ripple.size + 'px'
        }"
      ></span>
    </div>
  </component>
</template>

<script setup>
import { computed, ref } from 'vue';
import InlineSvg from 'vue-inline-svg';

const props = defineProps({
  as: {
    type: String,
    default: 'button',
    validator: (value) => ['router-link', 'button', 'a'].includes(value)
  },
  type: {
    type: String,
    default: 'mainBlack',
    validator: (value) => ['mainBlack', 'mainWhite', 'primary', 'outlined', 'secondary', 'danger', 'success'].includes(value)
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  text: {
    type: String,
    default: 'button'
  },
  iconType: {
    type: String,
    default: 'svg'
  },
  icon: String,
  showText: {
    type: Boolean,
    default: true
  },
  hasCounter: Boolean,
  counterNumber: [String, Number],
  disabled: Boolean,
  loading: Boolean,
  nowrap: Boolean,
  rounded: {
    type: Boolean,
    default: true
  },
  shadow: Boolean,
  ariaLabel: String,
  fontWeight: {
    type: String,
    default: 'font-normal'
  },
  classList: {
    type: String,
    default: ''
  },
  width: {
    type: String,
    default: 'w-4'
  },
  paddingStyle: {
    type: String,
    default: ''
  },
  hoverEffect: {
    type: String,
    default: 'none',
    validator: (value) => ['scale', 'slide', 'glow', 'underline', 'background', 'mysticAura', 'none'].includes(value)
  },
  svgAnimation: {
    type: String,
    default: 'none',
    validator: (value) => ['path', 'rotate', 'none'].includes(value)
  },
  rippleEffect: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['clickBtn']);

const ripples = ref([]);

const handleRipple = (e) => {
  if (!props.disabled && !props.loading) {
    emit('clickBtn', e);
  }
  if (!props.rippleEffect) return;
  
  const buttonRect = e.currentTarget.getBoundingClientRect();
  const size = Math.max(buttonRect.width, buttonRect.height);
  const x = e.clientX - buttonRect.left - size/2;
  const y = e.clientY - buttonRect.top - size/2;

  ripples.value.push({ x, y, size });
  
  setTimeout(() => {
    ripples.value = ripples.value.slice(1);
  }, 600);
};

const handleHover = () => {
  if (!props.disabled && !props.loading) {
    // Add any hover state logic here if needed
  }
};

const handleHoverEnd = () => {
  if (!props.disabled && !props.loading) {
    // Add any hover end logic here if needed
  }
};

const buttonClasses = computed(() => {
  const base = [
    props.classList,
    props.rounded ? 'rounded-[30px]' : 'rounded',
    props.shadow ? 'shadow-sm' : '',
    getSizeClasses(),
    getTypeClasses()
  ];
  
  return props.paddingStyle 
    ? [...base, props.paddingStyle] 
    : base.join(' ');
});

const textClasses = computed(() => [
  props.fontWeight,
  props.nowrap ? 'whitespace-nowrap' : '',
  props.type === 'outlined' ? 'text-mainBlack-1000' : 'text-white'
]);

const counterClasses = computed(() => ({
  'bg-primary-1000': props.type !== 'danger',
  'bg-danger-1000': props.type === 'danger'
}));

const iconClass = computed(() => [
  props.width,
  props.loading ? 'animate-pulse' : ''
]);

const getSizeClasses = () => ({
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg'
}[props.size]);

const getTypeClasses = () => ({
  mainBlack: 'bg-mainBlack-1000 hover:bg-mainBlack-800',
  primary: 'bg-primary-1000 hover:bg-primary-800',
  outlined: "whitespace-nowrap border cursor-pointer shadow-[0px_0px_0px_2px_rgba(0,0,0,0.2)] bg-white border-solid border-black",
  secondary: 'bg-secondary-1000 hover:bg-secondary-800',
  danger: 'bg-danger-1000 hover:bg-danger-800',
  success: 'bg-success-1000 hover:bg-success-800',
  mainWhite: 'bg-none text-black'
}[props.type]);

</script>

<style lang="scss" >
button.animate-svg-path svg path {
  stroke: white;
  stroke-width: 1;
  stroke-dasharray: 150;
  stroke-dashoffset: 150;
  transition: stroke-dashoffset 0.5s ease-in-out;
}

button.animate-svg-path:hover svg path {
  stroke-dashoffset: 0;
  fill: none;
}

button.animate-svg-rotate svg {
  transition: transform 0.6s ease-in-out;
}

button.animate-svg-rotate:hover svg {
  transform: rotate(360deg);
}
</style>
<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto:400,100,900');

// Animation mixins
@mixin btn-border-animation {
  border: 0 solid;
  box-shadow: inset 0 0 20px rgba(255, 255, 255, 0);
  outline: 1px solid;
  outline-color: rgba(255, 255, 255, .5);
  outline-offset: 0px;
  transition: all 1250ms cubic-bezier(0.19, 1, 0.22, 1);
}

@mixin btn-slide-border {
  position: relative;
  overflow: hidden;
  
  &:after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background: currentColor;
    transform: translateX(-100%);
    transition: transform 0.4s ease;
  }
}

.counter-badge {
  @apply absolute -top-2 -right-2 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold;
}

.loader {
  @apply w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin;
}

button:disabled {
  @apply cursor-not-allowed opacity-50;
}

[aria-busy="true"] {
  @apply cursor-wait;
}

@keyframes shine {
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
}

.hover\:shine:hover {
  background: linear-gradient(
    45deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,0.3) 50%,
    rgba(255,255,255,0) 100%
  );
  background-size: 200% auto;
  animation: shine 1.5s linear infinite;
}

[data-hover-animation="border"] {
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::before {
    opacity: 1;
  }
}

.hover\:border-animation {
  @include btn-border-animation;
  
  &:hover {
    border: 1px solid;
    box-shadow: inset 0 0 20px rgba(255, 255, 255, .5), 0 0 20px rgba(255, 255, 255, .2);
    outline-color: rgba(255, 255, 255, 0);
    outline-offset: 15px;
  }
}

.hover\:underline-animation {
  @include btn-slide-border;
  
  &:hover:after {
    transform: translateX(0);
  }
}

.hover\:glow-animation {
  transition: text-shadow 0.3s ease;
  
  &:hover {
    text-shadow: -1px -1px 0 rgba(0,0,0,0.2);
  }
}

.hover\:slide-border-animation {
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background: currentColor;
    transform: translateX(-100%);
    transition: transform 0.4s ease;
  }
  
  &:hover:before {
    transform: translateX(0);
  }
}

.bg-mainBlack-1000[data-hover-animation="border"]::before { border-color: theme('colors.mainBlack.1000') !important; }
.bg-primary-1000[data-hover-animation="border"]::before { border-color: theme('colors.primary.1000') !important; }
.bg-secondary-1000[data-hover-animation="border"]::before { border-color: theme('colors.secondary.1000') !important; }
.bg-danger-1000[data-hover-animation="border"]::before { border-color: theme('colors.primary.1000') !important; }
.bg-success-1000[data-hover-animation="border"]::before { border-color: theme('colors.secondary.1000') !important; }
.bg-white[data-hover-animation="border"]::before { border-color: theme('colors.mainBlack.1000') !important; }

.hover\:shadow-glow{
  transition: 0.5s ease all;
}
.hover\:shadow-glow:hover {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.45);
}

.hover\:mystic-aura {
  outline: 0px solid;
  outline-color: rgba(0, 0, 0, 0.5);
  outline-offset: 0px;
  transition: all 1250ms cubic-bezier(0.19, 1, 0.22, 1);
} 

.hover\:mystic-aura:hover {
  outline: 1px solid;
  outline-color: rgba(255, 255, 255, 0);
  outline-offset: 15px;
}

.hover\:after\:scale-x-100::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: currentColor;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.hover\:after\:scale-x-100:hover::after {
  transform: scaleX(1);
}

.hover\:bg-halo {
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 30px;
    background: linear-gradient(
      90deg,
      rgba(0,0,0,0) 0%,
      rgba(0,0,0,0.15) 50%,
      rgba(0,0,0,0.3) 100%
    );
    opacity: 0;
    transition: opacity 0.4s ease, transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateX(-100%);
    z-index: -1;
  }

  &:hover::before {
    opacity: 1;
    transform: translateX(0);
  }
  
  // Border animation
  &:hover {
    // box-shadow: inset 0 0 0 1px rgba(0,0,0,0.1);
  }
}

@keyframes ripple {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ripple {
  animation: ripple 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Add these hover effect styles */
.hover-scale:hover {
  transform: scale(1.05);
}

.hover-slide:hover {
  transform: translateX(5px);
}

.hover-glow:hover {
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
}

.hover-underline:hover::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background: currentColor;
  animation: underlineSlide 0.3s ease-out;
}

@keyframes underlineSlide {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}
</style> 